<template>
  <div class="customlcf-section">
    <div class="customlcf-section-header">
      <h3>{{ getTranslations('expression_1410') }}</h3>
    </div>

    <div v-if="loading" class="customlcf-loading">
      <div class="customlcf-spinner"></div>
      <span>{{ getTranslations('expression_36') }}</span>
    </div>

    <div v-else class="customlcf-editable-fields-container">
      <div v-if="!visibleFields.length" class="customlcf-no-fields">
        <i class="bx bx-list-ul"></i>
        <span>{{ getTranslations('expression_1411') }}</span>
      </div>

      <div v-else class="customlcf-editable-fields-grid">
        <div v-for="field in visibleFields" :key="field.id" class="customlcf-editable-field">
          <label :for="`field-${field.id}`" class="customlcf-field-label">{{ field.field_name }}</label>
          <div class="customlcf-input-group">
            <input :id="`field-${field.id}`"
                   :type="field.field_type === 'number' ? 'number' : 'text'"
                   v-model="field.value"
                   :placeholder="`${getTranslations('expression_1412')} ${field.field_name}`"
                   class="customlcf-field-input"
                   @change="updateField(field)" />
            <button class="customlcf-field-save-btn" @click="updateField(field)" :disabled="field.saving">
              <i :class="field.saving ? 'bx bx-loader-alt bx-spin' : 'bx bx-check'"></i>
            </button>
          </div>
        </div>
      </div>

      <div v-if="filteredFields.length > 2" class="customlcf-toggle-view">
        <button class="customlcf-toggle-btn" @click="showAll = !showAll">
          <span>{{ showAll ? getTranslations('expression_1409') : getTranslations('expression_63') }}</span>
          <i class="bx" :class="showAll ? 'bx-chevron-up' : 'bx-chevron-down'"></i>
        </button>
      </div>
    </div>
  </div>
</template>


<script>

export default {
  props: {
    leadId: { type: Number, required: true },
    channelId: { type: Number, required: true }
  },
  data() {
    return {
      customFields: [],
      leadFieldValues: [],
      loading: false,
      showAll: false,
      hasLoadedData: false,
      lastLoadedLeadId: null
    };
  },
  watch: {
    leadId: {
      immediate: true,
      handler(newLeadId, oldLeadId) {
        // Só carrega se o leadId mudou realmente
        if (newLeadId && newLeadId !== oldLeadId && newLeadId !== this.lastLoadedLeadId) {
          this.hasLoadedData = false;
          this.loadCustomFields();
        }
      }
    },
      channelId: {
      handler(newChannelId, oldChannelId) {
        // Só carrega se o channelId mudou
        if (newChannelId && newChannelId !== oldChannelId) {
          this.hasLoadedData = false;
          this.loadCustomFields();
        }
      }
    }
  },
  computed: {
    getTranslations() {
      return this.$store.getters.getTranslations;
    },
    filteredFields() {
      return this.customFields.filter(f => !f.hidden);
    },
    visibleFields() {
      return this.showAll ? this.filteredFields : this.filteredFields.slice(0, 2);
    }
  },
  methods: {
    async loadCustomFields() {
      if (this.hasLoadedData || this.loading || !this.leadId || !this.channelId) {
        return;
      }

      try {
        this.loading = true;
        this.lastLoadedLeadId = this.leadId;

        // Buscar campos do canal
        await this.$store.dispatch('fetchCustomFields', this.channelId);
        const channelFields = this.$store.state.customfield.customfields || [];

        // Buscar valores do lead
        const values = await this.$store.dispatch('fetchLeadCustomFieldValues', this.leadId) || [];

        // Combinar dados
        this.customFields = channelFields.map(field => {
          const value = values.find(v => Number(v.channel_custom_field_id) === Number(field.id));
          return { ...field, value: value ? value.value : '' };
        });

        this.hasLoadedData = true;
      } catch (error) {
        console.error('Erro ao carregar campos:', error);
      } finally {
        this.loading = false;
      }
    },
    async updateField(field) {
      try {
        this.$set(field, 'saving', true);
        await this.$store.dispatch('updateLeadCustomFieldValues', {
          leadId: this.leadId,
          fields: [{ custom_field_id: field.id, value: field.value }]
        });
        this.$swal.fire({
          icon: 'success',
          title: this.getTranslations('expression_1423'),
          timer: 1500,
          toast: true,
          position: 'bottom-end',
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Erro ao atualizar campo:', error);
        this.$swal.fire({
          icon: 'error',
          title: this.getTranslations('expression_1424'),
          text: error.message || this.getTranslations('expression_1426'),
          timer: 2000,
          toast: true,
          position: 'bottom-end',
          showConfirmButton: false
        });
      } finally {
        this.$set(field, 'saving', false);
      }
    }
  },
  mounted() {
    if (this.leadId && this.channelId) {
      this.loadCustomFields();
    }
  }
};
</script>


<style scoped>
/* Base styles */
.customlcf-section {
  margin-bottom: 24px;
  width: 100%;
}

.customlcf-section-header {
  margin-bottom: 16px;
}

.customlcf-section-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

/* Loading state */
.customlcf-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  color: #666;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.customlcf-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid #722ed1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Editable fields container */
.customlcf-editable-fields-container {
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden; /* Prevent overflow */
  width: 100%;
}

/* Grid layout for fields - Improved responsiveness */
.customlcf-editable-fields-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  width: 100%;
}

/* Field styling */
.customlcf-editable-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.customlcf-field-label {
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.customlcf-input-group {
  display: flex;
  position: relative;
  width: 100%;
}

.customlcf-field-input {
  width: 100%;
  padding: 10px 40px 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  background-color: white;
  overflow: hidden;
  text-overflow: ellipsis;
}

.customlcf-field-input:focus {
  outline: none;
  border-color: #722ed1;
  box-shadow: 0 0 0 3px rgba(114, 46, 209, 0.1);
}

.customlcf-field-input::placeholder {
  color: #aaa;
  font-style: italic;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.customlcf-field-save-btn {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #722ed1;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.customlcf-field-save-btn:hover {
  background-color: rgba(114, 46, 209, 0.1);
}

.customlcf-field-save-btn:disabled {
  color: #d3adf7;
  cursor: not-allowed;
}

.customlcf-field-save-btn i {
  font-size: 18px;
}

/* No fields message */
.customlcf-no-fields {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
  color: #888;
  font-size: 14px;
  border: 1px dashed #ddd;
  width: 100%;
}

.customlcf-no-fields i {
  font-size: 20px;
  color: #aaa;
}

/* Toggle view button */
.customlcf-toggle-view {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.customlcf-toggle-btn {
  background: none;
  border: none;
  color: #722ed1;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 16px;
  transition: all 0.2s ease;
}

.customlcf-toggle-btn:hover {
  background-color: rgba(114, 46, 209, 0.08);
}

.customlcf-toggle-btn i {
  font-size: 16px;
}

/* Improved responsive adjustments */
@media (max-width: 1200px) {
  .customlcf-editable-fields-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
}

@media (max-width: 992px) {
  .customlcf-editable-fields-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
}

@media (max-width: 768px) {
  .customlcf-editable-fields-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
  }
  
  .customlcf-field-input {
    padding: 8px 36px 8px 10px;
    font-size: 13px;
  }
  
  .customlcf-field-save-btn {
    width: 28px;
    height: 28px;
  }
  
  .customlcf-field-save-btn i {
    font-size: 16px;
  }
}

@media (max-width: 576px) {
  .customlcf-editable-fields-grid {
    grid-template-columns: 1fr;
  }
  
  .customlcf-editable-fields-container {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .customlcf-field-label {
    font-size: 12px;
  }
  
  .customlcf-field-input {
    padding: 8px 32px 8px 8px;
    font-size: 12px;
  }
  
  .customlcf-field-save-btn {
    width: 24px;
    height: 24px;
    right: 2px;
  }
  
  .customlcf-field-save-btn i {
    font-size: 14px;
  }
}
</style>